import { useEffect, useId, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import Badge from "components/badge/Badge";

const tabDomId = (base, id) => `${base}-tab-${id}`;
const panelDomId = (base, id) => `${base}-panel-${id}`;

const resolveActive = (tabs, candidates) =>
  candidates.find((c) => c != null && tabs.some((t) => t.id === c)) ?? tabs[0]?.id;

const TabList = ({ tabs, activeId, onSelect, id, "aria-label": ariaLabel, className = "" }) => {
  const generatedId = useId();
  const base = id ?? generatedId;
  const tabRefs = useRef({});

  const handleKeyDown = (event, index) => {
    const last = tabs.length - 1;
    const targets = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    };
    const nextIndex = targets[event.key];
    if (nextIndex === undefined) return;
    event.preventDefault();
    const nextId = tabs[nextIndex].id;
    tabRefs.current[nextId]?.focus();
    onSelect(nextId);
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation="horizontal"
      className={`flex gap-6 overflow-x-auto border-b border-line ${className}`.trim()}
    >
      {tabs.map((tab, index) => {
        const selected = tab.id === activeId;
        return (
          <button
            key={tab.id}
            ref={(node) => {
              tabRefs.current[tab.id] = node;
            }}
            type="button"
            role="tab"
            id={tabDomId(base, tab.id)}
            aria-selected={selected}
            aria-controls={id ? panelDomId(base, tab.id) : undefined}
            tabIndex={selected ? 0 : -1}
            onClick={() => onSelect(tab.id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={`focus-ring -mb-px inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-t-control border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
              selected
                ? "border-primary text-fg"
                : "border-transparent text-fg-muted hover:text-fg"
            }`}
          >
            {tab.label}
            {tab.count != null && (
              <Badge tone={selected ? "brand" : "neutral"} size="sm">
                {tab.count}
              </Badge>
            )}
          </button>
        );
      })}
    </div>
  );
};

const UrlSyncedTabs = ({ syncParam, tabs, value, onChange, ...rest }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeId = resolveActive(tabs, [searchParams.get(syncParam), value]);

  // Keep the parent's state aligned when the URL decides the tab (deep links, back/forward).
  useEffect(() => {
    if (activeId !== undefined && activeId !== value) onChange?.(activeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const select = (tabId) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(syncParam, tabId);
        return next;
      },
      { replace: true }
    );
    if (tabId !== value) onChange?.(tabId);
  };

  return <TabList {...rest} tabs={tabs} activeId={activeId} onSelect={select} />;
};

/**
 * Underline tabs with roving tabindex (ArrowLeft/Right, Home, End; selection
 * follows focus).
 *
 * @param {object} props
 * @param {{id: string, label: React.ReactNode, count?: number}[]} props.tabs
 * @param {string} [props.value] Active tab id; falls back to the first tab.
 * @param {(id: string) => void} [props.onChange] Called on selection. With `syncParam` it is
 *   also called when the URL resolves to a tab other than `value`.
 * @param {string} [props.syncParam] Query param to mirror, e.g. "tab" → `?tab=<id>`
 *   (history replace). A valid URL value wins over `value`. Requires a Router.
 * @param {string} [props.id] DOM id base. When set, tabs get `aria-controls` pointing at
 *   `<TabPanel tabsId={id} tabId=…>`; omit it if you don't render TabPanel.
 * @param {string} [props["aria-label"]] Accessible name of the tablist.
 * @param {string} [props.className]
 */
const Tabs = ({ syncParam, value, onChange, ...rest }) => {
  if (syncParam) {
    return <UrlSyncedTabs {...rest} syncParam={syncParam} value={value} onChange={onChange} />;
  }
  const activeId = resolveActive(rest.tabs, [value]);
  const select = (tabId) => {
    if (tabId !== value) onChange?.(tabId);
  };
  return <TabList {...rest} activeId={activeId} onSelect={select} />;
};

/**
 * Panel paired with `<Tabs id={tabsId}>`; render the active one.
 *
 * @param {object} props
 * @param {string} props.tabsId Same value passed to `Tabs` `id`.
 * @param {string} props.tabId Tab id this panel belongs to.
 * @param {string} [props.className]
 */
export const TabPanel = ({ tabsId, tabId, className = "", children }) => (
  <div
    role="tabpanel"
    id={panelDomId(tabsId, tabId)}
    aria-labelledby={tabDomId(tabsId, tabId)}
    tabIndex={0}
    className={`focus-ring rounded-card ${className}`.trim()}
  >
    {children}
  </div>
);

export default Tabs;
